import React, { useEffect, useRef, useState } from 'react';
import { FaCheck, FaSave } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../../utils/api';
import Lightbox from '../../../components/common/Lightbox';
import { ConfirmModal } from '../../../components/common/ConfirmModal';
import { assetsService } from '../../../services/assetsService';
import { useToast } from '../../../components/ui/ToastProvider';
import { parcelService } from '../../map/services/parcelService';
import { ParcelRequestDetailsModal } from '../../map/components/ParcelRequestDetailsModal';
import { ProfileSection } from '../../profile/components/ProfileSection';
import { AccountSection } from '../../profile/components/AccountSection';
import { MyStuffSection } from '../../profile/components/MyStuffSection';
import { ProfileHubShell } from '../../profile/components/ProfileHubShell';
import { getProfileSection } from '../../profile/profileTabs';
import { getCachedAssets, setCachedAssets } from '../../../utils/assetsCache';
import { useNotificationsUnread } from '../../notifications/hooks/useNotificationsUnread';
import '../../profile/profile-design.css';

export default function ProfilePage() {
  const { pathname } = useLocation();
  const section = getProfileSection(pathname);
  const navigate = useNavigate();
  const profileRef = useRef(null);
  const toastCtx = useToast();
  const showToast = toastCtx && typeof toastCtx.show === 'function' ? toastCtx.show : () => {};
  const { unreadTotal: unreadNotificationsTotal } = useNotificationsUnread();

  const [parcelRequests, setParcelRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [showParcelModal, setShowParcelModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [assets, setAssets] = useState(() => getCachedAssets('all') || []);
  const [assetsScope, setAssetsScope] = useState('all');
  const [monthlyUsed, setMonthlyUsed] = useState(null);
  const [monthlyLimit, setMonthlyLimit] = useState(null);
  const [showAssetsLightbox, setShowAssetsLightbox] = useState(false);
  const [assetsLightboxIndex, setAssetsLightboxIndex] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [assetPendingDelete, setAssetPendingDelete] = useState(null);
  const [saveState, setSaveState] = useState({ saving: false, savedFlash: false });

  useEffect(() => {
    if (section !== 'my-stuff') return undefined;
    const cached = getCachedAssets(assetsScope);
    if (cached) setAssets(cached);
    (async () => {
      try {
        const list = await assetsService.listMine(assetsScope);
        const rows = Array.isArray(list) ? list : [];
        setAssets(rows);
        setCachedAssets(assetsScope, rows);
      } catch (e) {
        if (!getCachedAssets(assetsScope)) setAssets([]);
      }
    })();
    return undefined;
  }, [section, assetsScope]);

  useEffect(() => {
    if (section !== 'my-stuff') return undefined;
    (async () => {
      try {
        const q = await assetsService.getQuota();
        setMonthlyUsed(Number(q?.used || 0));
        setMonthlyLimit(Number(q?.limit || 0) || null);
      } catch (_) {}
    })();
    return undefined;
  }, [section]);

  useEffect(() => {
    if (section !== 'my-stuff') return undefined;
    (async () => {
      try {
        setLoadingRequests(true);
        const list = await parcelService.listParcelRequests();
        setParcelRequests(Array.isArray(list) ? list : []);
      } catch (e) {
        setParcelRequests([]);
      } finally {
        setLoadingRequests(false);
      }
    })();
    return undefined;
  }, [section]);

  const openParcelModal = async (req) => {
    try {
      const fresh = await parcelService.getParcelRequest(req.id);
      setSelectedRequest(fresh || req);
    } catch (_) {
      setSelectedRequest(req);
    } finally {
      setShowParcelModal(true);
    }
  };

  const headerActions =
    section === 'profile' ? (
      <button
        type="button"
        className="phub-shell__save"
        disabled={saveState.saving}
        onClick={() => profileRef.current?.save?.()}
        data-testid="profile-hub-save"
      >
        {saveState.savedFlash ? (
          <FaCheck size={12} aria-hidden="true" />
        ) : (
          <FaSave size={12} aria-hidden="true" />
        )}
        <span>
          {saveState.savedFlash ? 'SAVED' : saveState.saving ? 'SAVING…' : 'SAVE'}
        </span>
      </button>
    ) : null;

  return (
    <div data-testid="profile-page">
      <ProfileHubShell
        activeId={section}
        unreadNotificationsTotal={unreadNotificationsTotal}
        actions={headerActions}
      >
        {section === 'profile' ? (
          <ProfileSection ref={profileRef} onSaveStateChange={setSaveState} />
        ) : null}
        {section === 'account' ? <AccountSection /> : null}
        {section === 'my-stuff' ? (
          <MyStuffSection
            assets={assets}
            assetsScope={assetsScope}
            onAssetsScopeChange={setAssetsScope}
            monthlyUsed={monthlyUsed}
            monthlyLimit={monthlyLimit}
            onPhotoClick={(asset) => {
              const idx = assets.findIndex((x) => x.id === asset.id);
              setAssetsLightboxIndex(idx >= 0 ? idx : 0);
              setShowAssetsLightbox(true);
            }}
            onDeletePhoto={(asset) => {
              setAssetPendingDelete(asset);
              setShowDeleteConfirm(true);
            }}
            onUseAsProfile={async (asset) => {
              try {
                await api.patch('/profiles/me/photo/', { asset_id: asset.id });
                showToast({ title: 'Profile photo updated', variant: 'success' });
              } catch (err) {
                console.error('Set profile from asset failed', err);
              }
            }}
            onLoadMore={async () => {
              try {
                const pageSize = 24;
                const nextPage = Math.floor(assets.length / pageSize) + 1 + 1;
                const data = await assetsService.listMinePaged(assetsScope, nextPage, pageSize);
                if (data && Array.isArray(data.results) && data.results.length > 0) {
                  setAssets((prev) => [...prev, ...data.results]);
                }
              } catch (e) {}
            }}
            parcelRequests={parcelRequests}
            loadingRequests={loadingRequests}
            onParcelClick={openParcelModal}
          />
        ) : null}
      </ProfileHubShell>

      <ParcelRequestDetailsModal
        show={showParcelModal}
        onHide={() => setShowParcelModal(false)}
        request={selectedRequest}
        onViewOnMap={(bbox) => {
          setShowParcelModal(false);
          const parts = [];
          if (bbox && Array.isArray(bbox) && bbox.length === 4) {
            parts.push(`parcel_bbox=${bbox.join(',')}`);
          }
          try {
            const f = selectedRequest?.result_fields || selectedRequest?.tile_properties || {};
            const ll = f.ll_uuid;
            const pid =
              selectedRequest?.parcel_id || f.parcelnumb || f.parcel_id || f.fid || f.ogc_fid;
            const ppath = selectedRequest?.path || f.path;
            if (ll) parts.push(`parcel_ll=${encodeURIComponent(ll)}`);
            else if (pid) parts.push(`parcel_id=${encodeURIComponent(String(pid))}`);
            else if (ppath) parts.push(`parcel_path=${encodeURIComponent(ppath)}`);
            if ((!bbox || !Array.isArray(bbox) || bbox.length !== 4) && f.lon && f.lat) {
              const lng = Number(f.lon);
              const lat = Number(f.lat);
              if (!Number.isNaN(lng) && !Number.isNaN(lat)) {
                parts.push(`parcel_center=${lng},${lat}`);
              }
            }
          } catch (_) {}
          const qs = parts.length > 0 ? `?${parts.join('&')}` : '';
          navigate(`/maps${qs}`);
        }}
      />

      <ConfirmModal
        show={showDeleteConfirm}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setAssetPendingDelete(null);
        }}
        onConfirm={async () => {
          if (!assetPendingDelete) return;
          try {
            await assetsService.deleteAsset(assetPendingDelete.id);
            const list = await assetsService.listMine(assetsScope);
            const rows = Array.isArray(list) ? list : [];
            setAssets(rows);
            setCachedAssets(assetsScope, rows);
            setMonthlyUsed(Array.isArray(list) ? list.length : 0);
            showToast({ title: 'Photo deleted', variant: 'success' });
          } catch (err) {
            const code = err?.response?.status;
            if (code === 400 && err?.response?.data?.error === 'in_use') {
              showToast({
                title: 'Cannot delete photo',
                body: 'Photo is in use on your profile or a harvest. Remove it there first.',
                variant: 'danger',
              });
            } else {
              showToast({ title: 'Delete failed', variant: 'danger' });
            }
          } finally {
            setShowDeleteConfirm(false);
            setAssetPendingDelete(null);
          }
        }}
        title="Delete photo"
        body="Are you sure you want to delete this photo? This action cannot be undone."
        confirmLabel="Delete"
        confirmVariant="danger"
      />

      {showAssetsLightbox ? (
        <Lightbox
          items={assets.map((a) => ({
            caption: a.scope || '',
            url: a.url,
            getUrl: async () => a.url,
          }))}
          initialIndex={assetsLightboxIndex}
          onClose={() => setShowAssetsLightbox(false)}
        />
      ) : null}
    </div>
  );
}
